import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createProject,
  deleteProject as deleteProjectApi,
  fetchAssignedProjects,
  fetchProjectDetailByCode,
  fetchProjectDetailByUuid,
  ProjectDetail,
  updateProjectDelta,
} from '../../../../infrastructure/api/project'
import {
  createInitialValue,
  fromDetail,
  toCreateInput,
  toDeleteInput,
  toDeltaInput,
} from '../model'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { extractValuesFromResponse } from '../../../../lib/commons/api'
import { handleWarning } from '../../../../handlers/globalErrorHandler'
import { setAssignedProject } from '../../../../store/project'
import { open } from '../../../router'
import { getPathByExternalId, APPLICATION_FUNCTION_EXTERNAL_ID } from '../..'
import { useProjectFormState } from './useProjectFormState'

// プロジェクト登録画面（テナントメニュー）の data フック。
// 作成＋更新＋削除に対応し、作成後は登録したコードの編集画面へ遷移する。
// NOTE: APPLICATION_FUNCTION_EXTERNAL_ID はモジュールトップレベルで読むと
// pages/index.tsx との循環参照で初期化前アクセス（undefined）になるため、
// 必ず実行時（コールバック内）で参照する。
export const useProjectEditSingleSheetData = (code: string) => {
  const dispatch = useDispatch()
  // code が無ければ新規登録、あれば編集。
  const isCreating = !code
  const [initialized, setInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [entity, setEntity] = useState<ProjectDetail | undefined>()
  const {
    formModel,
    setFormModel,
    formModelRef,
    onChangeProject,
    edited,
    refreshedPageData,
    editedFormData,
    refreshEditedFormData,
  } = useProjectFormState()

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      if (isCreating) {
        setEntity(undefined)
        setFormModel(createInitialValue())
      } else {
        const detail = await fetchProjectDetailByCode(code)
        setEntity(detail)
        setFormModel(fromDetail(detail))
      }
    } finally {
      setInitialized(true)
      setIsLoading(false)
      refreshedPageData()
      refreshEditedFormData()
    }
  }, [code, isCreating, setFormModel, refreshedPageData, refreshEditedFormData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refreshAssignedProjects = useCallback(async () => {
    dispatch(setAssignedProject(await fetchAssignedProjects()))
  }, [dispatch])

  const showSuccessMessage = useCallback(
    (titleId: string) => {
      dispatch(
        addScreenMessage(
          APPLICATION_FUNCTION_EXTERNAL_ID.PROJECT_REGISTRATION,
          {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: titleId }),
          }
        )
      )
    },
    [dispatch]
  )

  const submit = useCallback(async () => {
    const currentFormModel = formModelRef.current
    setIsLoading(true)
    try {
      if (isCreating) {
        const created = await createProject(toCreateInput(currentFormModel))
        await refreshAssignedProjects()
        showSuccessMessage('create.completed.title')
        refreshedPageData()
        // 作成したプロジェクトのコードで編集画面へ遷移する。作成APIは uuid のみ
        // 返す（コードは未入力時サーバー自動採番）ため、詳細を取得してコードを得る。
        if (created.uuid) {
          const detail = await fetchProjectDetailByUuid(created.uuid)
          if (detail.code) {
            open(
              `${getPathByExternalId(
                APPLICATION_FUNCTION_EXTERNAL_ID.PROJECT_REGISTRATION
              )}/${detail.code}`
            )
            return
          }
        }
        await fetchData()
        return
      }
      if (!entity) return
      const response = await updateProjectDelta(
        toDeltaInput(entity, currentFormModel)
      )
      // ステータス変更等がサーバー側で拒否されると 200 + ワーニングメッセージが
      // 返る（更新は反映されない）ため、成功メッセージは出さずに警告を表示する。
      if (response.hasWarning) {
        const messages = extractValuesFromResponse(response.json, 'messages')
        handleWarning(messages, _ => ({
          code: currentFormModel.code,
          name: currentFormModel.displayName,
        }))
        return
      }
      await refreshAssignedProjects()
      showSuccessMessage('update.completed.title')
      await fetchData()
    } finally {
      setIsLoading(false)
    }
  }, [
    isCreating,
    entity,
    fetchData,
    refreshAssignedProjects,
    refreshedPageData,
    showSuccessMessage,
    formModelRef,
  ])

  const deleteProject = useCallback(async () => {
    if (!entity) return
    setIsLoading(true)
    try {
      await deleteProjectApi(toDeleteInput(formModelRef.current))
      await refreshAssignedProjects()
      dispatch(
        addScreenMessage(
          APPLICATION_FUNCTION_EXTERNAL_ID.PROJECT_REGISTRATION,
          {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'project.registration.delete.completed.title',
            }),
          }
        )
      )
      refreshedPageData()
      open(
        getPathByExternalId(APPLICATION_FUNCTION_EXTERNAL_ID.PROJECTS_SEARCH)
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    entity,
    refreshAssignedProjects,
    refreshedPageData,
    dispatch,
    formModelRef,
  ])

  return {
    initialized,
    isCreating,
    isLoading,
    edited,
    editedFormData,
    formModel,
    entity,
    onChangeProject,
    submit,
    reload: fetchData,
    deleteProject,
  }
}
